import React, { createContext, FC, useContext, useRef } from "react";
import { TranslationProvider } from "Providers/TranslationProvider";
import AuthView from "Components/Auth/AuthView";
import { AuthProvider } from "Components/Auth/AuthProvider";
import SnackbarProvider from "./SnackbarProvider";
import { color, useMuiTheme } from "Configs/theme";
import { ThemeProvider } from "@material-ui/core";
import { app, apps, initializeApp, storage } from "firebase/app";
import environment from "Configs/environment";
import { CSSProperties, jssPreset, StylesProvider } from "@material-ui/styles";
import ScopedCssBaseline from "@material-ui/core/ScopedCssBaseline";
import { create as createJss } from "jss";
import TrackingProvider from "./TrackingProvider";
import InitProvider from "./InitProvider";

interface App {
  isStandaloneApp: boolean;
  shadowRoot?: ShadowRoot;
  mountPoint?: HTMLDivElement;
  firebaseApp?: app.App;
  firebaseStorage?: storage.Storage;
  token?: string | null;
}

interface Props {
  isStandaloneApp: boolean;
  shadowRoot: ShadowRoot;
  mountPoint: HTMLDivElement;
  reactRoot: HTMLDivElement;
  token: string | null;
  organizationIdForAppInit: string | null;
}

const AppContext = createContext<App>({
  isStandaloneApp: false,
});

const AppProvider: FC<Props> = (props) => {
  const muiTheme = useMuiTheme(props.shadowRoot);
  const jssRef = useRef(
    createJss({
      ...jssPreset(),
      insertionPoint: props.reactRoot,
    })
  );

  const firebaseAppRef = useRef(
    apps.length ? apps[0] : initializeApp(environment.firebaseConfig)
  );

  const firebaseStorageRef = useRef(
    firebaseAppRef.current.storage(environment.googleCloudStorageBucket)
  );

  const app: App = {
    isStandaloneApp: props.isStandaloneApp,
    shadowRoot: props.shadowRoot,
    mountPoint: props.mountPoint,
    firebaseApp: firebaseAppRef.current,
    firebaseStorage: firebaseStorageRef.current,
    token: props.token
  };

  return (
    <AppContext.Provider value={app}>
      <ScopedCssBaseline style={baselineCss}>
        <StylesProvider jss={jssRef.current}>
          <TrackingProvider>
            <ThemeProvider theme={muiTheme}>
              <TranslationProvider>
                <SnackbarProvider>
                  <AuthProvider>
                    <InitProvider organizationId={props.organizationIdForAppInit}>
                      <AuthView/>
                    </InitProvider>
                  </AuthProvider>
                </SnackbarProvider>
              </TranslationProvider>
            </ThemeProvider>
          </TrackingProvider>
        </StylesProvider>
      </ScopedCssBaseline>
    </AppContext.Provider>
  );
};

export default AppProvider;

export function useApp() {
  return useContext(AppContext);
}

const baselineCss: CSSProperties = {
  fontFamily: "Montserrat",
  lineHeight: 1.43,
  color: color.text,
  display: "flex",
  flexDirection: "column",
  flex: 1,
  fontSize: "0.875rem",
  letterSpacing: "0.01071em",
};
